import React, { createContext, useEffect, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import { initStateObject, objectReducer, LOADING, OVERWRITE, FAIL } from 'reducers/default'
import eventEvaluationApi from 'domain/eventEvaluation/api'
import { useAttributionModel } from 'domain/events/attributionModel/EventAttributionModelContextProvider'

export const EventFbInsightsContext = createContext(null)
export const EventFbInsightsDispatchContext = createContext(null)
export const CampaignStatsContext = createContext(null)

export function EventStatsProvider({ eventId, children }) {
  const [fbInsights, dispatchFbInsights] = useReducer(objectReducer, initStateObject)
  const [campaignResults, dispatchCampaignResults] = useReducer(objectReducer, initStateObject)
  const [hasCampaignStats, setHasCampaignStats] = useState(null)
  const { attributionModel } = useAttributionModel()

  useEffect(() => {
    // refetch results and insights when attribution model changes — the
    // model isn't sent as a request param, it's a persisted per-event
    // setting the backend already keys off server-side (matches v3 behavior).
    if (!eventId) return
    let cancelled = false

    dispatchCampaignResults({ type: LOADING })
    eventEvaluationApi.fetchCampaignResultsAsync(eventId, 'all').then((res) => {
      if (cancelled) return
      if (res.error) {
        // Two dispatches, same as the pre-v4 code: OVERWRITE first so
        // content.error_code survives alongside the FAIL flag (consumers
        // like EventStatistics.jsx's isFdLiveCollecting/isShortCampaign
        // read campaignResults.content?.error_code, not just `.error`).
        dispatchCampaignResults({
          type: OVERWRITE,
          payload: { error_code: res.error.code, message: res.error.message },
        })
        dispatchCampaignResults({ type: FAIL, payload: res.error.message })
        return
      }
      dispatchCampaignResults({ type: OVERWRITE, payload: res.data })
    })

    dispatchFbInsights({ type: LOADING })
    eventEvaluationApi.fetchInsightsAsync(eventId, 'all').then((res) => {
      if (cancelled) return
      if (res.error) {
        dispatchFbInsights({ type: FAIL, payload: res.error.message })
        return
      }
      dispatchFbInsights({ type: OVERWRITE, payload: res.data })
    })

    return () => {
      cancelled = true
    }
  }, [eventId, attributionModel])

  useEffect(() => {
    const fbInsightsValues = Object.values(fbInsights.content)
    const eventResultsValues = Object.values(campaignResults.content?.eventResults || {})
    const seriesResultsValues = Object.values(campaignResults.content?.seriesResults || {})
    let isFilled = campaignResults.error && fbInsights.error ? false : null

    if (fbInsightsValues.length || (eventResultsValues.length && seriesResultsValues.length)) {
      const isEventStats = eventResultsValues.some((el) => el)
      const isEventSeriesStats = seriesResultsValues.some((el) => el)
      const impressions = fbInsights.content?.impressions
      const clicks = fbInsights.content?.clicks
      const adSpend = fbInsights.content?.adSpend
      isFilled = isEventStats || isEventSeriesStats || !!impressions || !!clicks || !!adSpend
    }
    setHasCampaignStats(isFilled)
  }, [fbInsights, campaignResults])

  return (
    <EventFbInsightsContext.Provider value={fbInsights.content}>
      <EventFbInsightsDispatchContext.Provider value={dispatchFbInsights}>
        <CampaignStatsContext.Provider value={hasCampaignStats}>{children}</CampaignStatsContext.Provider>
      </EventFbInsightsDispatchContext.Provider>
    </EventFbInsightsContext.Provider>
  )
}

EventStatsProvider.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
}
